<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatDelay, highlightVariables } from './followUpTime';
import { isMoveStep, moveTargetLabels } from './followUpMove';

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits([
  'toggleExpand',
  'togglePause',
  'duplicate',
  'remove',
]);

const { t } = useI18n();

const isTemplate = computed(() => props.step.message_type === 'template');

const isMedia = computed(() =>
  ['image', 'document', 'video', 'audio'].includes(props.step.message_type)
);

const isAiAudio = computed(() => props.step.message_type === 'ai_audio');

// Passo "quem escreve" (motor novo): substitui a prévia do texto por um
// resumo do que a IA vai fazer ou da tarefa deixada pro vendedor — não dá
// pra mostrar o texto final porque ele ainda não foi escrito.
const isAiWriter = computed(() => props.step.mode === 'ai');
const isTaskWriter = computed(() => props.step.mode === 'task');

// Passo "Mover card": não é mensagem, então nada de balão do WhatsApp.
const isMove = computed(() => isMoveStep(props.step));
const moveLabels = computed(() =>
  moveTargetLabels(props.step, props.funnels, props.funnelId)
);

const aiIntentLabel = computed(() =>
  t(`FUNNEL_TIMELINE.INTENT_${(props.step.intent || 'retomar').toUpperCase()}`)
);

// Passo entregue no evento de entrada na etapa: não tem espera para mostrar.
const isInstant = computed(() => !!props.step.on_stage_enter);

// O "Dia N" saiu daqui na Task 4: virou redundante com o nó da
// FollowUpTimeline, que já mostra o dia acumulado na linha do tempo.
const delayLabel = computed(() =>
  t('FUNNEL_TIMELINE.WAITS', { t: formatDelay(props.step.delay_hours) })
);

// Rótulo do chip de tipo: cobre os tipos novos (mídia e áudio da IA), não só
// texto/template. Tipo desconhecido (passo antigo ou onda futura) cai em Texto.
const TYPE_LABEL_KEYS = {
  template: 'FUNNEL_TIMELINE.TYPE_TEMPLATE',
  image: 'FUNNEL_TIMELINE.TYPE_IMAGE',
  document: 'FUNNEL_TIMELINE.TYPE_DOCUMENT',
  video: 'FUNNEL_TIMELINE.TYPE_VIDEO',
  audio: 'FUNNEL_TIMELINE.TYPE_AUDIO',
  ai_audio: 'FUNNEL_TIMELINE.TYPE_AI_AUDIO',
};

const typeLabel = computed(() => {
  if (isMove.value) return t('FUNNEL_TIMELINE.TYPE_MOVE');
  return t(
    TYPE_LABEL_KEYS[props.step.message_type] || 'FUNNEL_TIMELINE.TYPE_TEXT'
  );
});

const channelInbox = computed(
  () => props.inboxes.find(inbox => inbox.id === props.step.inbox_id) || null
);

const bodyTokens = computed(() => highlightVariables(props.step.content));

const pauseIcon = computed(() =>
  props.step.paused ? 'i-lucide-play' : 'i-lucide-pause'
);

const pauseTooltip = computed(() =>
  props.step.paused ? t('FUNNEL_TIMELINE.RESUME') : t('FUNNEL_TIMELINE.PAUSE')
);

// Horário/tique fake da prévia — não é dado real, então fica fora do i18n
// (mesmo texto em qualquer idioma, como no WhatsApp de verdade).
const previewTimestamp = '10:32 ✓✓';
</script>

<template>
  <div
    :data-testid="`follow-up-step-card-${index}`"
    class="cursor-pointer rounded-xl border border-n-weak bg-n-solid-2 transition-all hover:border-n-brand/40 hover:shadow-md"
    :class="{
      'opacity-60': step.paused,
      'border-n-brand/50 ring-2 ring-n-brand/30': expanded,
    }"
    @click="emit('toggleExpand')"
  >
    <div class="flex flex-wrap items-center gap-2 px-3 pt-3">
      <span
        v-if="isInstant"
        class="flex items-center gap-1 rounded-md bg-n-brand/15 px-2 py-0.5 text-xs font-medium text-n-brand"
      >
        <span class="i-lucide-zap size-3" />
        {{ t('FUNNEL_TIMELINE.INSTANT') }}
      </span>
      <span
        v-else
        class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        <span class="i-lucide-clock size-3" />
        {{ delayLabel }}
      </span>
      <span
        class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        {{ typeLabel }}
      </span>
      <span
        v-if="channelInbox"
        class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        <span class="i-lucide-send size-3" />
        {{ channelInbox.name }}
      </span>
      <span
        v-if="step.paused"
        class="rounded-md bg-n-amber-3 px-2 py-0.5 text-xs font-medium text-n-amber-11"
      >
        {{ t('FUNNEL_TIMELINE.PAUSED') }}
      </span>

      <div class="ml-auto flex items-center gap-1">
        <Button
          v-tooltip="pauseTooltip"
          variant="ghost"
          color="slate"
          size="sm"
          :icon="pauseIcon"
          data-testid="follow-up-step-card-pause"
          @click.stop="emit('togglePause')"
        />
        <Button
          v-tooltip="t('FUNNEL_TIMELINE.DUPLICATE')"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-copy"
          data-testid="follow-up-step-card-duplicate"
          @click.stop="emit('duplicate')"
        />
        <Button
          v-tooltip="t('FUNNEL_TIMELINE.DELETE')"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          data-testid="follow-up-step-card-remove"
          @click.stop="emit('remove')"
        />
      </div>
    </div>

    <div v-if="isMove" class="px-3 pb-3 pt-2">
      <div
        data-testid="follow-up-step-card-move"
        class="flex items-center gap-2 rounded-lg border border-dashed border-n-weak px-3 py-2 text-sm text-n-slate-12"
      >
        <span class="i-lucide-arrow-right-left size-4 shrink-0 text-n-brand" />
        <span v-if="moveLabels">
          {{ t('FUNNEL_TIMELINE.CARD_MOVE', moveLabels) }}
          <span
            v-if="step.move_funnel_id && step.move_keep_in_source"
            class="text-n-slate-11"
          >
            {{ t('FUNNEL_TIMELINE.CARD_MOVE_KEEP') }}
          </span>
        </span>
        <span v-else class="text-n-ruby-11">
          {{ t('FUNNEL_TIMELINE.CARD_MOVE_MISSING') }}
        </span>
      </div>
    </div>
    <div v-else class="px-3 pb-3 pt-2">
      <div class="flex justify-end">
        <div
          class="max-w-[85%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-2 text-sm text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]"
        >
          <div v-if="isAiWriter" data-testid="follow-up-step-card-ai">
            <p class="flex items-center gap-1.5 font-medium">
              <span class="i-lucide-sparkles size-4 shrink-0" />
              {{ t('FUNNEL_TIMELINE.CARD_AI', { intent: aiIntentLabel }) }}
            </p>
            <p
              v-if="step.hint"
              class="mt-1 whitespace-pre-wrap break-words text-xs opacity-80"
            >
              {{ step.hint }}
            </p>
          </div>
          <p
            v-else-if="isTaskWriter"
            data-testid="follow-up-step-card-task"
            class="flex items-center gap-1.5 font-medium"
          >
            <span class="i-lucide-list-checks size-4 shrink-0" />
            {{ t('FUNNEL_TIMELINE.CARD_TASK', { text: step.task_text }) }}
          </p>
          <p
            v-else-if="isTemplate"
            class="flex items-center gap-1.5 font-medium"
          >
            <span class="i-lucide-layout-template size-4 shrink-0" />
            {{ step.template_name }}
          </p>
          <template v-else-if="isMedia">
            <img
              v-if="step.message_type === 'image' && step.media_url"
              data-testid="follow-up-step-card-image"
              :src="step.media_url"
              alt=""
              class="mb-1 max-h-32 rounded"
            />
            <p v-else class="flex items-center gap-1.5 font-medium">
              <span
                class="size-4 shrink-0"
                :class="
                  step.message_type === 'audio'
                    ? 'i-lucide-mic'
                    : step.message_type === 'video'
                      ? 'i-lucide-video'
                      : 'i-lucide-file-text'
                "
              />
              {{
                step.media_name ||
                (step.message_type === 'audio'
                  ? t('FUNNEL_TIMELINE.VOICE_NOTE')
                  : t('FUNNEL_TIMELINE.MEDIA_MISSING'))
              }}
            </p>
            <p v-if="step.content" class="whitespace-pre-wrap break-words">
              {{ step.content }}
            </p>
          </template>
          <div v-else-if="isAiAudio" data-testid="follow-up-step-card-audio">
            <p class="flex items-center gap-2 font-medium">
              <span class="i-lucide-audio-lines size-4" />
              {{ t('FUNNEL_TIMELINE.AI_AUDIO') }}
            </p>
            <p class="mt-1 whitespace-pre-wrap break-words text-xs opacity-80">
              <template
                v-for="(token, tokenIndex) in bodyTokens"
                :key="tokenIndex"
              >
                <span
                  v-if="token.type === 'var'"
                  class="rounded bg-black/10 px-1 font-medium dark:bg-white/20"
                  >{{ token.value }}</span
                >
                <template v-else>{{ token.value }}</template>
              </template>
            </p>
          </div>
          <p v-else class="whitespace-pre-wrap break-words">
            <template
              v-for="(token, tokenIndex) in bodyTokens"
              :key="tokenIndex"
            >
              <span
                v-if="token.type === 'var'"
                class="rounded bg-black/10 px-1 font-medium dark:bg-white/20"
                >{{ token.value }}</span
              >
              <template v-else>{{ token.value }}</template>
            </template>
          </p>
          <p class="mt-1 text-right text-[10px] opacity-60">
            {{ previewTimestamp }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
